import { createClient } from '@supabase/supabase-js'

// Supabase configuration (landing + leads)
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'YOUR_SUPABASE_URL'
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'YOUR_SUPABASE_ANON_KEY'

// Supabase configuration (iOS app admin tables: videos + parent_profiles)
// NOTE: you must set these env vars in the website app for the admin tables to work.
// Add them to your `.env` as:
// REACT_APP_SUPABASE_URL_KID=...
// REACT_APP_SUPABASE_ANON_KEY_KID=...
const supabaseKidUrl =
  process.env.REACT_APP_SUPABASE_URL_KID || supabaseUrl
const supabaseKidAnonKey =
  process.env.REACT_APP_SUPABASE_ANON_KEY_KID || supabaseAnonKey

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Create Supabase client for the iOS-backed admin tables
export const supabaseKid = createClient(supabaseKidUrl, supabaseKidAnonKey)

// Function to insert lead data
export const insertLead = async (leadData) => {
  try {
    const { data, error } = await supabase
      .from('leads')
      .insert([
        {
          name: leadData.name,
          email: leadData.email,
          phone: leadData.phone,
          number_of_kids: leadData.numberOfKids,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
      ])
      .select()

    if (error) {
      console.error('Error inserting lead:', error)
      throw error
    }

    console.log('Lead inserted successfully:', data)
    return { success: true, data }
  } catch (error) {
    console.error('Error in insertLead:', error)
    return { success: false, error: error.message }
  }
}

// Function to get all leads (for admin purposes)
export const getLeads = async () => {
  try {
    const { data, error } = await supabase
      .from('leads')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching leads:', error)
      throw error
    }

    return { success: true, data }
  } catch (error) {
    console.error('Error in getLeads:', error)
    return { success: false, error: error.message }
  }
}

// -----------------------
// iOS admin tables (Kid)
// -----------------------

export const getVideos = async ({ category, content_appropriateness } = {}) => {
  try {
    let query = supabaseKid
      .from('videos')
      .select('*')
      .order('created_at', { ascending: false })

    if (category && category.trim().length > 0) {
      query = query.eq('category', category.trim())
    }
    if (content_appropriateness && content_appropriateness.trim().length > 0) {
      query = query.eq('content_appropriateness', content_appropriateness.trim())
    }

    const { data, error } = await query
    if (error) throw error

    return { success: true, data }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

export const createVideo = async (videoData) => {
  try {
    const now = new Date().toISOString()
    const payload = {
      title: videoData.title,
      youtube_url: videoData.youtube_url,
      category: videoData.category,
      content_language: videoData.content_language ?? null,
      description: videoData.description ?? null,
      content_appropriateness: videoData.content_appropriateness,
      created_at: now,
      updated_at: now,
    }

    const { data, error } = await supabaseKid
      .from('videos')
      .insert([payload])
      .select()
      .single()

    if (error) throw error
    return { success: true, data }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

export const updateVideo = async (id, videoData) => {
  try {
    const now = new Date().toISOString()
    const payload = {
      title: videoData.title,
      youtube_url: videoData.youtube_url,
      category: videoData.category,
      content_language: videoData.content_language ?? null,
      description: videoData.description ?? null,
      content_appropriateness: videoData.content_appropriateness,
      updated_at: now,
    }

    const { data, error } = await supabaseKid
      .from('videos')
      .update(payload)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return { success: true, data }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

export const deleteVideo = async (id) => {
  try {
    const { data, error } = await supabaseKid
      .from('videos')
      .delete()
      .eq('id', id)

    if (error) throw error
    return { success: true, data }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

export const getParentProfiles = async () => {
  try {
    const { data, error } = await supabaseKid
      .from('parent_profiles')
      .select(
        'id,email,first_name,last_name,is_premium,subscription_expires_at,subscription_product_id,revenuecat_user_id,subscription_status'
      )
      .order('subscription_expires_at', { ascending: false })

    if (error) throw error
    return { success: true, data }
  } catch (error) {
    return { success: false, error: error.message }
  }
}
